import dayjs, { Dayjs } from "dayjs";
import { isObject } from "lodash";
import { TestContext } from "yup";

import { MAIN_TIME_FORMAT } from "../../constants";
import {
  ICustomField,
  ICustomValidationParams,
  IValidationYupUnit,
  IValidationYupUnitSiblingParams,
  siblingRequiredCustomValidation,
} from "../../models";
import { Log } from "../log.helpers";

interface IGetSiblingValueTimeArguments {
  value: string;
  siblingFieldName?: string;
  context: TestContext;
}

export const getSiblingValueTime = ({
  siblingFieldName,
  context,
  value,
}: IGetSiblingValueTimeArguments): null | {
  valueTime: Dayjs;
  siblingTime: Dayjs;
} => {
  if (!siblingFieldName) {
    Log.error(`Missed sibling name for ${context.path}`);
    return null;
  }

  const siblingValue = context.parent[siblingFieldName];

  if (siblingValue === undefined || siblingValue === null) {
    return null;
  }

  const siblingTime = dayjs(siblingValue, MAIN_TIME_FORMAT);
  const valueTime = dayjs(value as string, MAIN_TIME_FORMAT);

  if (!siblingTime.isValid()) {
    Log.warn(
      `Invalid sibling time for ${context.path}, sibling value: ${siblingValue}, sibling field name: ${siblingFieldName}`,
    );
    return null;
  }

  if (!valueTime.isValid()) {
    Log.warn(`Invalid value time for ${context.path}, value: ${value}`);
    return null;
  }

  return { valueTime, siblingTime };
};

export const isSiblingRequiredValidationRule = (
  validationRule: IValidationYupUnit,
): boolean => {
  const param = validationRule.params?.at(0);
  return isObject(param) && "ref" in param;
};

export const getSiblingRequiredValidationRuleFieldName = (
  validationRule: IValidationYupUnit,
): null | ICustomField["name"] => {
  if (isSiblingRequiredValidationRule(validationRule)) {
    return (validationRule.params?.at(0) as IValidationYupUnitSiblingParams)
      ?.ref;
  }

  if (
    validationRule.validation === "custom" &&
    siblingRequiredCustomValidation.has(
      (validationRule.params?.at(0) as ICustomValidationParams)
        .customValidationName,
    )
  ) {
    return (
      (validationRule?.params?.at(0) as ICustomValidationParams)
        ?.customValidationAdditional?.siblingName ?? null
    );
  }

  return null;
};

interface IGetSiblingToValidationFieldsArguments {
  controls: ICustomField[];
  formFields: ICustomField["name"][];
}

export const getSiblingToValidationFields = ({
  controls,
  formFields,
}: IGetSiblingToValidationFieldsArguments): string[] => {
  if (!formFields.length) {
    return [];
  }

  const result = formFields.reduce((acc, dirtyField) => {
    const formField = controls.find(control => control.name === dirtyField);

    if (!formField) {
      return acc;
    }

    formField.formValidation?.rules?.forEach(validationRule => {
      const siblingValidationParamRequiredField =
        getSiblingRequiredValidationRuleFieldName(validationRule);

      if (!siblingValidationParamRequiredField) {
        return;
      }

      acc.push(siblingValidationParamRequiredField);
    });

    return acc;
  }, [] as string[]);

  return result;
};
